import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useId} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {Caption} from "@khanacademy/wonder-blocks-typography";
import {a11yStyles} from "@khan/shared-styles";

interface Props {
    setSettings: () => void;
    label?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    hasUnexpectedError?: boolean;
    maybeReauthError?: string | null;
    testId?: string;
}

export const SaveChangesButton: React.FC<Props> = ({
    setSettings,
    label = t`Save changes`,
    isLoading,
    isDisabled,
    hasUnexpectedError,
    maybeReauthError,
    testId,
}) => {
    const saveChangesWarningId = useId();
    const reloadMessage = t`Saving changes will reload the page.`;
    return (
        <View style={styles.buttonContainer}>
            <Tooltip placement="right" content={reloadMessage}>
                <View>
                    <Button
                        onClick={setSettings}
                        style={styles.button}
                        spinner={isLoading}
                        disabled={isDisabled}
                        data-testid={testId}
                        aria-describedby={saveChangesWarningId}
                    >
                        {label}
                    </Button>
                    <Caption
                        id={saveChangesWarningId}
                        style={a11yStyles.srOnly}
                    >
                        {reloadMessage}
                    </Caption>
                </View>
            </Tooltip>
            {hasUnexpectedError && (
                <Caption style={styles.error}>
                    {t`Oops. Something went wrong. Please try again.`}
                </Caption>
            )}
            {maybeReauthError && (
                <Caption style={styles.error}>{maybeReauthError}</Caption>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    buttonContainer: {
        flexDirection: "row",
        marginBlockStart: sizing.size_240,
    },
    button: {
        alignSelf: "flex-start",
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
    error: {
        color: semanticColor.status.critical.foreground,
        marginInlineStart: sizing.size_160,
    },
});
